import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    Switch,
    IconButton,
    Tooltip,
    Divider
} from '@mui/material';
import {
    Public as GlobalIcon,
    Home as CustomIcon,
    Share as ShareIcon,
    FamilyRestroom as FamilyRestroomIcon,
    HelpOutline as HelpIcon
} from '@mui/icons-material';
import { useCreatePresetRequestMutation } from '../../../../store/services/delivery-service';
import MainCard from "../../../../components/cards/MainCard";
import TenancySelectionComponent from "../../../../components/TenancySelectionComponent";
import { getMonthDifference, getInclusiveDayCount, MODAL_BORDER_RADIUS } from '../../utils/dateUtils';

/**
 * Modal component for exporting calendar items to a preset template
 **/
const ExportToPresetModal = ({
    open,
    onClose,
    calendarItems,
    showNotification
}) => {
    const [presetName, setPresetName] = useState('');
    const [presetItemsWithPattern, setPresetItemsWithPattern] = useState([]);
    const [exportAttempted, setExportAttempted] = useState(false);
    // New states for sharing options
    const [sharedTenancyIds, setSharedTenancyIds] = useState([]);
    const [publishToFamily, setPublishToFamily] = useState(false);
    const [isGlobalPreset, setIsGlobalPreset] = useState(false);

    // Get tenancy info and user role from Redux store
    const { tenancyId } = useSelector((state) => state.tenancy);
    const { role } = useSelector((state) => state.auth.user || {});

    // Check if user is super admin
    const isSuperAdmin = role === "Administrators";

    const [createPresetRequest, { isLoading: isCreatingPreset }] = useCreatePresetRequestMutation();

    // Helper function to get display name for content
    const getContentDisplayName = (item) => {
        if (item.contentType === 'RandomSimulation') {
            return item.contentName || 'Random Simulation Collection';
        }
        if (item.contentType === 'ContentPack') {
            return item.contentName || 'Content Pack';
        }
        return item.contentName || item.contentId;
    };

    // Helper function to get display content type
    const getDisplayContentType = (contentType) => {
        switch (contentType) {
            case 'RandomSimulation':
                return 'Random Simulation';
            case 'Simulation':
                return 'Simulation';
            case 'Course':
                return 'Course';
            case 'ContentPack':
                return 'Content Pack';
            default:
                return contentType;
        }
    };

    // Helper function to get chip color for content type
    const getContentTypeColor = (contentType) => {
        switch (contentType) {
            case 'Simulation':
            case 'RandomSimulation':
                return 'primary';
            case 'Course':
            case 'ContentPack':
                return 'warning';
            default:
                return 'default';
        }
    };

    // Reset form state when modal opens
    useEffect(() => {
        if (open) {
            setPresetName('');
            // Set default sharing settings
            if (isSuperAdmin) {
                setSharedTenancyIds([0]); // Default to global for admins
                setIsGlobalPreset(true);
            } else {
                setSharedTenancyIds([tenancyId]); // Default to current tenancy for non-admins
                setIsGlobalPreset(false);
            }
            setPublishToFamily(false);
            setExportAttempted(false);
        }
    }, [open, isSuperAdmin, tenancyId]);

    // Calculate month-day patterns when the modal opens with calendar items
    useEffect(() => {
        if (open && calendarItems.length > 0) {
            const sortedItems = [...calendarItems].sort((a, b) =>
                new Date(a.scheduledFor) - new Date(b.scheduledFor)
            );

            // Find the earliest date
            const earliestDate = new Date(sortedItems[0].scheduledFor);
            earliestDate.setHours(0, 0, 0, 0); // Normalize time

            // Calculate relative month and day of month for each item
            const itemsWithMonthDay = sortedItems.map(item => {
                const itemDate = new Date(item.scheduledFor);
                itemDate.setHours(0, 0, 0, 0); // Normalize time

                // Calculate relative month (1-based)
                const monthDiff = getMonthDifference(earliestDate, itemDate);
                const relativeMonth = monthDiff + 1; // Make it 1-based

                // Get day of month
                const dayOfMonth = itemDate.getDate();

                return {
                    ...item,
                    relativeMonth,
                    dayOfMonth,
                    adjustForMonthEnd: true,
                    adjustForWeekends: false
                };
            });

            setPresetItemsWithPattern(itemsWithMonthDay);
        }
    }, [open, calendarItems]);

    // Handle change in shared tenancies
    const handleSharedTenanciesChange = (tenancyIds) => {
        setSharedTenancyIds(tenancyIds);
        setIsGlobalPreset(tenancyIds.includes(0));

        // If global is selected, disable publish to family
        if (tenancyIds.includes(0)) {
            setPublishToFamily(false);
        }
    };

    const handleCreatePreset = async () => {
        setExportAttempted(true);

        if (!presetName.trim()) {
            return;
        }

        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const userId = user?.email || '';

            const presetData = {
                name: presetName,
                createdById: userId,
                sharedTenancyIds: isSuperAdmin ? sharedTenancyIds : [tenancyId],
                publishToFamily: isSuperAdmin ? publishToFamily : false,
                presetItems: presetItemsWithPattern.map(item => {
                    // Calculate batch duration days from scheduledEndDate if available.
                    // Inclusive count: a same-day batch (start === end) is 1 day, not 0.
                    const batchDurationDays = item.isBatchSend && item.scheduledEndDate && item.scheduledFor
                        ? getInclusiveDayCount(item.scheduledFor, item.scheduledEndDate)
                        : null;

                    return {
                        contentId: item.contentId, // This preserves internalId for RandomSimulation
                        contentType: item.contentType, // This preserves 'RandomSimulation'
                        contentName: item.contentName, // This preserves collection name for display
                        relativeMonth: item.relativeMonth,
                        dayOfMonth: item.dayOfMonth,
                        adjustForMonthEnd: true,
                        adjustForWeekends: false,
                        // Batch send fields
                        isBatchSend: item.isBatchSend || false,
                        batchDurationDays: batchDurationDays,
                        sendingTimeStart: item.isBatchSend ? (item.sendingTimeStart || '') : '',
                        sendingTimeEnd: item.isBatchSend ? (item.sendingTimeEnd || '') : '',
                        pauseWeekends: item.isBatchSend ? (item.pauseWeekends || false) : false
                    };
                })
            };

            await createPresetRequest(presetData).unwrap();
            showNotification(`Template created successfully`, 'success');
            onClose();
        } catch (error) {
            console.error('Error creating template:', error);
            showNotification(`Error creating template: ${error.data?.error || 'Unknown error'}`, 'error');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: { borderRadius: MODAL_BORDER_RADIUS },
                sx: {
                    bgcolor: 'background.default',
                    maxWidth: '1080px' // ~20% wider than default md (900px)
                }
            }}
        >
            <DialogTitle>
                <Typography variant="h4" >
                    Save as Journey Template
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1, mb: 3 }}>
                    <TextField
                        label="Template Name"
                        value={presetName}
                        onChange={(e) => setPresetName(e.target.value)}
                        fullWidth
                        required
                        error={exportAttempted && !presetName.trim()}
                        helperText={exportAttempted && !presetName.trim() ? "Name is required" : ""}
                    />
                </Box>

                {/* Template Type Display */}
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ mr: 1 }}>
                        Template Type:
                    </Typography>
                    <Chip
                        icon={isGlobalPreset ? <GlobalIcon /> : <CustomIcon />}
                        label={isGlobalPreset ? "Global Template" : "Custom Template"}
                        color={isGlobalPreset ? "primary" : "default"}
                        size="small"
                    />
                </Box>

                {/* Sharing Settings - only for admins */}
                {isSuperAdmin && (
                    <Box sx={{ mt: 3, mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="h4" gutterBottom>
                            Template Sharing Settings
                        </Typography>

                        {/* Tenancy Selection Component */}
                        <Box sx={{ mb: 2 }}>
                            <TenancySelectionComponent
                                selectedTenancies={sharedTenancyIds}
                                onChange={handleSharedTenanciesChange}
                            />
                        </Box>

                        {/* Publish to Family Toggle */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="body1">Share with Tenancy Family</Typography>
                                <Tooltip title="When enabled, this template will be accessible to all descendants of the selected tenancies" placement="top">
                                    <IconButton size="small">
                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Switch
                                checked={publishToFamily}
                                onChange={(e) => setPublishToFamily(e.target.checked)}
                                inputProps={{ "aria-label": "publish to family" }}
                                disabled={isGlobalPreset}
                            />
                        </Box>
                    </Box>
                )}

                <Typography variant="h6" gutterBottom>
                    Content Items ({presetItemsWithPattern.length})
                </Typography>

                <MainCard
                    content={true}
                    boxShadow={true}
                    border={false}
                    sx={{ bgcolor: 'background.default' }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Month</TableCell>
                                <TableCell>Day</TableCell>
                                <TableCell>Content Type</TableCell>
                                <TableCell>Content Name</TableCell>
                                <TableCell>Delivery</TableCell>
                                <TableCell>Original Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {presetItemsWithPattern.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Chip
                                            label={`Month ${item.relativeMonth}`}
                                            color={"default"}
                                            variant={"outlined"}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={`Day ${item.dayOfMonth}`}
                                            color="secondary"
                                            variant="outlined"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getDisplayContentType(item.contentType)}
                                            color={getContentTypeColor(item.contentType)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{getContentDisplayName(item)}</TableCell>
                                    <TableCell>
                                        {item.isBatchSend ? (
                                            <Tooltip title={
                                                <Box>
                                                    <Typography variant="caption" display="block">
                                                        Time: {item.sendingTimeStart || '00:00'} - {item.sendingTimeEnd || '23:59'}
                                                    </Typography>
                                                    <Typography variant="caption" display="block">
                                                        Pause weekends: {item.pauseWeekends ? 'Yes' : 'No'}
                                                    </Typography>
                                                </Box>
                                            }>
                                                <Chip
                                                    label="Batch"
                                                    color="secondary"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Tooltip>
                                        ) : (
                                            <Chip
                                                label="Direct"
                                                color="default"
                                                variant="outlined"
                                                size="small"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>{new Date(item.scheduledFor).toLocaleDateString()}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </MainCard>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        This template follows a month/day pattern, with Month 1 representing the first month when the template is applied.
                        {presetItemsWithPattern.some(item => item.contentType === 'RandomSimulation') && (
                            <span> Random Simulation items will select a random simulation from their associated collection when sent.</span>
                        )}
                        {presetItemsWithPattern.some(item => item.contentType === 'ContentPack') && (
                            <span> Content Pack items will deliver all courses from the pack when sent.</span>
                        )}
                        {presetItemsWithPattern.some(item => item.isBatchSend) && (
                            <span> Batch send settings will be preserved for applicable items.</span>
                        )}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    color="secondary"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleCreatePreset}
                    variant="contained"
                    color="primary"
                    disabled={isCreatingPreset}
                >
                    {isCreatingPreset ? (
                        <CircularProgress size={24} />
                    ) : (
                        `Save as Template`
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ExportToPresetModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    calendarItems: PropTypes.array.isRequired,
    showNotification: PropTypes.func.isRequired
};

export default ExportToPresetModal;